import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Leaf, LogOut, Images, Calendar, Newspaper, Type, Users, KeyRound, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MediaManager from './MediaManager';
import EventsManager from './EventsManager';
import PostsManager from './PostsManager';
import UsersManager from './UsersManager';
import { ContentManager, PasswordManager } from './SettingsManager';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === 'owner';
  const [tab, setTab] = useState('media');

  const tabs = [
    { key: 'media', label: 'Media', icon: Images, show: true },
    { key: 'events', label: 'Events', icon: Calendar, show: isOwner },
    { key: 'posts', label: 'News', icon: Newspaper, show: isOwner },
    { key: 'content', label: 'Homepage', icon: Type, show: isOwner },
    { key: 'users', label: 'Team', icon: Users, show: isOwner },
    { key: 'password', label: 'Password', icon: KeyRound, show: true },
  ].filter((t) => t.show);

  const doLogout = () => { logout(); navigate('/admin'); };

  return (
    <div className="min-h-screen bg-cream">
      {/* Top bar */}
      <header className="bg-forest text-cream sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 rounded-full bg-forest-light"><Leaf className="w-5 h-5 text-gold-light" strokeWidth={1.6} /></span>
            <div className="leading-tight">
              <p className="font-display text-sm text-white">Owner Portal</p>
              <p className="text-[11px] text-gold-light">{user?.username} · {user?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-2 text-sm text-cream/80 hover:text-white"><ExternalLink className="w-4 h-4" /> View site</Link>
            <button onClick={doLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold text-white text-sm font-600 hover:bg-gold-light"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <h1 className="font-display text-3xl text-forest mb-6">Manage your website</h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-sand pb-4">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-600 transition-all ${tab === t.key ? 'bg-forest text-cream' : 'bg-white text-forest border border-sand hover:border-forest'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'media' && <MediaManager />}
        {tab === 'events' && isOwner && <EventsManager />}
        {tab === 'posts' && isOwner && <PostsManager />}
        {tab === 'content' && isOwner && <ContentManager />}
        {tab === 'users' && isOwner && <UsersManager />}
        {tab === 'password' && <PasswordManager />}
      </div>
    </div>
  );
};

export default Dashboard;
